/**
 * Driver-side cap for `page.evaluate` (and other unbounded Playwright ops).
 *
 * Playwright's `page.evaluate` has NO default timeout, and any in-page
 * deadline (e.g. `__waitDomQuiet`'s `maxWaitMs`) is enforced with page-side
 * timers — which never fire when the page's main thread is blocked. The
 * June 2026 live-snapshot rollout wedged entire suite runs this way: one
 * stalled page turned every subsequent evaluate/screenshot/goto into an
 * indefinite hang because nothing on the driver side ever gave up.
 *
 * This helper races the op against a driver-side timer, which fires
 * regardless of renderer state. On cap it resolves to {@link EVALUATE_CAPPED}
 * — the orphaned op keeps running in the browser and settles (or dies with
 * the page) later; its post-cap rejection is swallowed so it can never
 * surface as an unhandled rejection.
 *
 * @module
 */

/** Sentinel resolved when the driver-side cap fires before the op settles. */
export const EVALUATE_CAPPED = Symbol('browser-core.evaluate-capped');

/**
 * Run `op()` bounded by a driver-side `capMs` timer.
 *
 * Resolves with the op's value, or {@link EVALUATE_CAPPED} if the cap fires
 * first (blocked main thread, dead CDP channel). Pre-cap rejections
 * (navigation, context destroyed) propagate to the caller as usual — only
 * the orphaned-after-cap rejection is swallowed.
 */
export async function evaluateWithCap<R>(
  capMs: number,
  op: () => Promise<R>
): Promise<R | typeof EVALUATE_CAPPED> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let capped = false;
  const cap = new Promise<typeof EVALUATE_CAPPED>((resolve) => {
    timer = setTimeout(() => {
      capped = true;
      resolve(EVALUATE_CAPPED);
    }, capMs);
  });
  const evaluation: Promise<R | typeof EVALUATE_CAPPED> = op().catch(
    (err: unknown): typeof EVALUATE_CAPPED => {
      // After the cap fires the caller has moved on — swallow the orphan's
      // rejection (it would otherwise be an unhandled rejection). Before the
      // cap, propagate: callers distinguish "page navigated" from "capped".
      if (capped) return EVALUATE_CAPPED;
      throw err;
    }
  );
  try {
    return await Promise.race([evaluation, cap]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
